import {Changed, Icon, TippyJsx as Tippy} from '@bitstillery/common/components/portal'
import {MithrilComponent} from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'
import {format_money_responsive} from '@bitstillery/common/lib/format'
import {$t} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'
import type {SalesOrderAdditional} from '@bitstillery/common/models/sales_order'

import {$m, $s} from '@/app'
import {IncentivePromotion, IncentiveTransport} from '@/components/incentives'
import {panel_cart_collapsed} from '@/components/panels/panel_cart/panel_cart_state'

export class TotalCosts extends MithrilComponent {
    /**
     * One line: both bottle and case totals (no tooltip). Cart may show Changed when validated qty differs.
     */
    units_line(bottles: number, cases: number, prev?: {bottles: number; cases: number}): m.Children {
        const pb = prev?.bottles ?? bottles
        const pc = prev?.cases ?? cases
        const bottle_changed = bottles !== pb
        const case_changed = cases !== pc

        const bottle_num = bottle_changed ? (
            <Changed className='totals-units-changed' format_as_number={true} previous={pb} current={bottles} type='warning' />
        ) : (
            <span className='totals-units-value'>{bottles}</span>
        )
        const case_num = case_changed ? (
            <Changed className='totals-units-changed' format_as_number={true} previous={pc} current={cases} type='warning' />
        ) : (
            <span className='totals-units-value'>{cases}</span>
        )

        return (
            <div className='details details-units-line'>
                <div className='details-units-line-inner'>
                    <span className='units-pair'>
                        {bottle_num} <span className='units-label'>{$t('unit.bottle', {count: bottles})}</span>
                    </span>
                    <span aria-hidden='true' className='units-sep'>
                        ·
                    </span>
                    <span className='units-pair'>
                        {case_num} <span className='units-label'>{$t('unit.case', {count: cases})}</span>
                    </span>
                </div>
            </div>
        )
    }

    /**
     * Money amount, showing a Changed diff when the validated value differs from
     * the current value (cart was re-validated and a quantity/price moved).
     */
    amount(current: number, validated: number): m.Children {
        if (current !== validated) {
            return (
                <Changed
                    className='fl-column-reverse gap-0'
                    currency={$s.identity.user.currency}
                    previous={current}
                    current={validated}
                    type='warning'
                />
            )
        }
        return format_money_responsive(validated, null, $s.identity.user.currency)
    }

    /**
     * One totals row: icon chip + label on the left, amount on the right, optional
     * units subtitle below. `kind` drives the `subtotal-${kind}` modifier class.
     */
    subtotal_row(opts: {
        kind: string
        icon: string
        label: m.Children
        tip: string
        amount: m.Children
        units?: m.Children
    }): m.Children {
        return (
            <div className={`subtotal subtotal-${opts.kind}`}>
                <div className='header'>
                    {this.collapsed_row_tip(opts.tip, [
                        <div className='description'>
                            <div className='icon-wrapper'>
                                <Icon name={opts.icon} />
                            </div>
                            <span>{opts.label}</span>
                        </div>,
                        <div className='amount'>{opts.amount}</div>,
                    ])}
                </div>
                {opts.units}
            </div>
        )
    }

    /** Collapsed rail: wrap icon + amount for hover tooltip; expanded layout unchanged. */
    collapsed_row_tip(tip: string, inner: m.Children): m.Children {
        if (!panel_cart_collapsed()) {
            return inner
        }

        if (globalThis.__SSR_MODE__ || typeof document === 'undefined') {
            return inner
        }

        return (
            <Tippy
                appendTo={document.body}
                className='c-total-costs-collapsed-tip'
                content={tip}
                interactive={false}
                placement='left'
            >
                <div className='c-total-costs-collapsed-tip-trigger'>{inner}</div>
            </Tippy>
        )
    }

    view(_vn: m.Vnode) {
        const active_voucher = $m.cart.voucher_additional()
        const booked_items = [...$s.cart.sales_order_items, ...$s.cart.sales_order_tbo_items]
        const min_order = Number($s.identity.user.minimum_order_amount)
        const currency = $s.identity.user.currency

        return (
            <div
                className={classes('c-total-costs', {
                    warning: $m.cart.has_errors(),
                })}
            >
                {(Boolean(min_order) || $s.promotion.active) && (
                    <div className='incentives'>
                        {Boolean(min_order) && <IncentiveTransport />}
                        {$s.promotion.active && [<IncentivePromotion />]}
                    </div>
                )}

                {booked_items.length > 0 &&
                    this.subtotal_row({
                        kind: 'booked',
                        icon: 'booked',
                        label: $t('cart.soi.booked_title', {booked_items: booked_items.length}),
                        tip: $t('checkout.totals_collapsed_tip_booked'),
                        amount: format_money_responsive($m.cart.booked_amount(), null, currency),
                        units: this.units_line($m.cart.booked_bottles(), $m.cart.booked_cases()),
                    })}

                {this.subtotal_row({
                    kind: 'cart',
                    icon: 'cartRight',
                    label: $t('checkout.cart'),
                    tip: $t('checkout.totals_collapsed_tip_cart'),
                    amount: this.amount($m.cart.cart_amount(), $m.cart.cart_amount_validated()),
                    units: this.units_line($m.cart.cart_bottles_validated(), $m.cart.cart_cases_validated(), {
                        bottles: $m.cart.cart_bottles(),
                        cases: $m.cart.cart_cases(),
                    }),
                })}

                {active_voucher && active_voucher.value_type === 'PERCENTAGE' && this.voucher_row(active_voucher)}

                {this.subtotal_row({
                    kind: 'vat',
                    icon: 'money',
                    label: $t('checkout.vat'),
                    tip: $t('checkout.totals_collapsed_tip_vat'),
                    amount: this.amount($m.cart.vat_total(), $m.cart.vat_total_validated()),
                })}

                {active_voucher && active_voucher.value_type === 'FIXED' && this.voucher_row(active_voucher)}

                {this.subtotal_row({
                    kind: 'total',
                    icon: 'invoice',
                    label: $t('checkout.total'),
                    tip: $t('checkout.totals_collapsed_tip_total'),
                    amount: this.amount($m.cart.total_amount(), $m.cart.total_amount_validated()),
                })}
            </div>
        )
    }

    voucher_row(active_voucher: SalesOrderAdditional) {
        return this.subtotal_row({
            kind: 'voucher',
            icon: 'voucherOn',
            label: active_voucher.description,
            tip: $t('checkout.totals_collapsed_tip_voucher'),
            amount: format_money_responsive(Number(active_voucher.total)),
        })
    }
}
